import React, { useState, useEffect, useRef, useCallback } from 'react';
import humanChatService from '../../services/humanChatService';
import ChatMessageInput from './ChatMessageInput';
import '../../styles/group-chat-brutal.css';

/**
 * Chat thread component - displays messages and input
 * Brutal design style
 */
const ChatThread = ({
  conversation,
  currentUserId,
  onBack,
  onMessageSent
}) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Get conversation display name
  const getConversationName = () => {
    if (!conversation) return 'Chat';

    if (conversation.type === 'group') {
      return conversation.name || 'Group Chat';
    }

    const other = humanChatService.getOtherParticipant(conversation, currentUserId);
    return other?.nickname || other?.username || 'Chat';
  };

  // Load messages
  const loadMessages = useCallback(async (before = null) => {
    if (!conversation?._id) return;

    try {
      if (before) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const options = { limit: 50 };
      if (before) options.before = before;

      const data = await humanChatService.getMessages(conversation._id, options);

      if (data.success) {
        if (before) {
          // Prepend older messages
          setMessages(prev => [...data.messages, ...prev]);
        } else {
          setMessages(data.messages || []);
        }
        setHasMore(data.hasMore);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [conversation?._id]);

  // Initial load
  useEffect(() => {
    if (conversation?._id) {
      loadMessages();
    }
  }, [conversation?._id, loadMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (!loading && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // Poll for new messages
  useEffect(() => {
    if (!conversation?._id) return;

    const pollNewMessages = async () => {
      if (document.hidden) return;

      try {
        const lastMessage = messages[messages.length - 1];
        if (!lastMessage) return;

        const data = await humanChatService.getMessages(conversation._id, {
          after: lastMessage._id,
          limit: 50
        });

        if (data.success && data.messages?.length > 0) {
          setMessages(prev => [...prev, ...data.messages]);
        }
      } catch (err) {
        console.error('Failed to poll messages:', err);
      }
    };

    const interval = setInterval(pollNewMessages, 5000);
    return () => clearInterval(interval);
  }, [conversation?._id, messages]);

  // Handle send message
  const handleSendMessage = async (content) => {
    if (!content.trim() || sending) return;

    setSending(true);

    // Optimistic update
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      _id: tempId,
      conversationId: conversation._id,
      senderId: { _id: currentUserId },
      content: content.trim(),
      type: 'text',
      createdAt: new Date().toISOString(),
      isPending: true
    };

    setMessages(prev => [...prev, tempMessage]);

    try {
      const data = await humanChatService.sendMessage(conversation._id, content.trim());

      if (data.success) {
        // Replace temp message with real one
        setMessages(prev =>
          prev.map(m => m._id === tempId ? data.message : m)
        );
        onMessageSent?.(data.message);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      // Remove failed message
      setMessages(prev => prev.filter(m => m._id !== tempId));
      alert(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle load more
  const handleLoadMore = () => {
    if (hasMore && !loadingMore && messages.length > 0) {
      loadMessages(messages[0]._id);
    }
  };

  // Format message time
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Check if message is from current user
  const isOwnMessage = (message) => {
    const senderId = message.senderId?._id || message.senderId;
    return String(senderId) === String(currentUserId);
  };

  // Get sender display name
  const getSenderName = (message) => {
    if (isOwnMessage(message)) return 'You';
    return message.senderId?.nickname || message.senderId?.username || 'Unknown';
  };

  if (loading) {
    return (
      <div className="chat-thread">
        <ThreadHeader
          name={getConversationName()}
          onBack={onBack}
        />
        <div className="chat-messages">
          <div className="chat-messages-loading">LOADING...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-thread">
        <ThreadHeader
          name={getConversationName()}
          onBack={onBack}
        />
        <div className="chat-error">
          <div className="chat-error-icon">⚠️</div>
          <div className="chat-error-title">Failed to load</div>
          <div className="chat-error-text">{error}</div>
          <button className="chat-retry-btn" onClick={() => loadMessages()}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-thread">
      <ThreadHeader
        name={getConversationName()}
        onBack={onBack}
        participantCount={conversation?.participants?.length}
        isGroup={conversation?.type === 'group'}
      />

      <div className="chat-messages" ref={messagesContainerRef}>
        {/* Load more button */}
        {hasMore && (
          <div className="load-more-messages">
            <button
              className="load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'LOADING...' : 'LOAD OLDER'}
            </button>
          </div>
        )}

        {/* Empty state */}
        {messages.length === 0 && (
          <div className="conversation-empty">
            <div className="conversation-empty-icon">💬</div>
            <div className="conversation-empty-title">No messages yet</div>
            <div className="conversation-empty-text">
              Start the conversation!
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((message, index) => {
          const isOwn = isOwnMessage(message);
          const senderName = getSenderName(message);
          const senderInitial = senderName[0]?.toUpperCase() || '?';

          return (
            <div
              key={message._id}
              className={`message-bubble ${isOwn ? 'sent' : 'received'} ${message.isPending ? 'pending' : ''} ${message.type === 'system' ? 'system' : ''}`}
            >
              {!isOwn && (
                <div className="message-sender-row">
                  <div className="sender-avatar-mini">{senderInitial}</div>
                  <div className="message-sender">{senderName}</div>
                </div>
              )}
              {isOwn && (
                <div className="message-sender-row own">
                  <div className="message-sender">You</div>
                </div>
              )}
              <div className="message-content">{message.content}</div>
              <div className="message-time">
                {message.isPending ? 'Sending...' : formatTime(message.createdAt)}
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      <ChatMessageInput
        onSend={handleSendMessage}
        disabled={sending}
        placeholder="Type a message..."
      />
    </div>
  );
};

/**
 * Thread header component
 */
const ThreadHeader = ({ name, onBack, participantCount, isGroup }) => (
  <div className="chat-thread-header">
    <button className="chat-thread-back" onClick={onBack} aria-label="Back to conversations">
      ←
    </button>
    <div className="chat-thread-info">
      <h3 className="chat-thread-name">{name}</h3>
      {isGroup && participantCount && (
        <div className="chat-thread-status">{participantCount} members</div>
      )}
    </div>
  </div>
);

export default ChatThread;
